import { TriangleLeftIcon } from "@radix-ui/react-icons";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/core/helpers/cn";
import { MouseIcon, StarIcon } from "@/shared/icons";
import { Button } from "@/shared/ui";
import { FactionEnum } from "@/domains/faction/constants/factionEnum";
import { factionsList } from "@/domains/faction/helpers/factionsList";
import FactionImprovementInfo from "@/domains/faction/components/FactionImprovementInfo";
import FactionImprovementsLine from "@/domains/faction/components/FactionImprovementsLine";
import FactionUpgradeNode from "@/domains/faction/components/FactionUpgradeNode";

type Props = {
  currentCount: number;
  maxCount: number;
  faction: FactionEnum;
  onBack: () => void;
  setCount: Dispatch<SetStateAction<number>>;
};

export default function FactionImprovementsContainer({
  currentCount,
  maxCount,
  faction,
  onBack,
  setCount,
}: Props) {
  const { t } = useTranslation("faction");

  const [levels, setLevels] = useState<(0 | 1 | 2 | 3)[]>([0, 0, 0, 0, 0, 0, 0, 0]);
  const [isInfo, setIsInfo] = useState(false);

  const U1 = 0;
  const U2 = 1;
  const U3 = 2;
  const U4 = 3;
  const U5 = 4;
  const U6 = 5;
  const U7 = 6;
  const U8 = 7;

  const acquired = useCallback((idx: number) => levels[idx] === 3, [levels]);

  const parents: Record<number, number[]> = {
    [U1]: [],
    [U2]: [],
    [U3]: [U1, U2],
    [U4]: [U1, U2],
    [U5]: [U3, U4],
    [U6]: [U5],
    [U7]: [U5],
    [U8]: [U6, U7],
  };

  const exclusiveGroups: number[][] = [
    [U1, U2],
    [U3, U4],
    [U6, U7],
  ];
  const groupOf = (idx: number) => exclusiveGroups.find((g) => g.includes(idx));

  const isLocked = useCallback(
    (idx: number): boolean => {
      const ps = parents[idx] ?? [];
      if (ps.length > 0) {
        const anyParentAcquired = ps.some((p) => acquired(p));
        if (!anyParentAcquired) return true;
      }
      const g = groupOf(idx);
      if (g) {
        const others = g.filter((i) => i !== idx);
        if (others.some((i) => levels[i] > 0) && levels[idx] === 0) return true;
      }
      return false;
    },
    [levels, acquired],
  );

  const step = useCallback(
    (idx: number) => {
      if (isLocked(idx)) return;
      if (levels[idx] >= 3) return;
      if (currentCount <= 0) return;

      setLevels((prev) => {
        const next = [...prev] as (0 | 1 | 2 | 3)[];
        next[idx] = Math.min(3, next[idx] + 1) as 0 | 1 | 2 | 3;
        return next;
      });
      setCount((c) => Math.max(0, c - 1));
    },
    [isLocked, levels, currentCount, setCount],
  );

  const row1LeftActive = acquired(U1);
  const row1RightActive = acquired(U2);
  const row2LeftActive = acquired(U3);
  const row2RightActive = acquired(U4);
  const centerDownActive = acquired(U5);
  const row4LeftActive = acquired(U6);
  const row4RightActive = acquired(U7);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef<{ x: number; y: number; left: number; top: number }>({
    x: 0,
    y: 0,
    left: 0,
    top: 0,
  });
  const movedRef = useRef(false);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    movedRef.current = false;
    setIsPanning(true);
    panStart.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop };
  }, []);

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isPanning || !scrollRef.current) return;
      const dx = e.clientX - panStart.current.x;
      const dy = e.clientY - panStart.current.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) movedRef.current = true;
      scrollRef.current.scrollLeft = panStart.current.left - dx;
      scrollRef.current.scrollTop = panStart.current.top - dy;
    },
    [isPanning],
  );

  const endPan = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    (e.target as Element).releasePointerCapture?.(e.pointerId);
    setIsPanning(false);
  }, []);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
      el.scrollTop = (el.scrollHeight - el.clientHeight) / 2;
    }
  }, []);

  return (
    <div className="absolute z-[5] top-0 left-0 flex flex-col size-full bg-dark-900">
      <div
        ref={scrollRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPan}
        onPointerCancel={endPan}
        onClickCapture={(e) => {
          if (movedRef.current) {
            e.preventDefault();
            e.stopPropagation();
            movedRef.current = false;
          }
        }}
        className={cn(
          "absolute top-0 left-0 w-full h-full flex justify-center items-center overflow-auto p-5 no-scrollbar",
          isPanning ? "cursor-grabbing select-none" : "cursor-grab",
        )}
      >
        {isInfo && <FactionImprovementInfo />}
        <div className="w-max flex flex-col justify-center items-center">
          <div className="relative flex justify-between w-[16.0625rem] mb-7.5">
            <FactionImprovementsLine leftActive={row1LeftActive} rightActive={row1RightActive} />
            <FactionUpgradeNode
              level={levels[U1]}
              onClick={() => step(U1)}
              colorOne={factionsList[faction]?.colorAccent}
              colorTwo={factionsList[faction]?.colorGradient2}
              isLocked={isLocked(U1)}
            />
            <FactionUpgradeNode
              level={levels[U2]}
              onClick={() => step(U2)}
              colorOne={factionsList[faction]?.colorAccent}
              colorTwo={factionsList[faction]?.colorGradient2}
              isLocked={isLocked(U2)}
            />
          </div>

          <div className="relative flex justify-between w-[16.0625rem] mb-6">
            <FactionImprovementsLine
              leftActive={row2LeftActive}
              rightActive={row2RightActive}
              className="h-[2.9375rem]"
            />
            <FactionUpgradeNode
              level={levels[U3]}
              onClick={() => step(U3)}
              colorOne={factionsList[faction]?.colorAccent}
              colorTwo={factionsList[faction]?.colorGradient2}
              isLocked={isLocked(U3)}
            />
            <FactionUpgradeNode
              level={levels[U4]}
              onClick={() => step(U4)}
              colorOne={factionsList[faction]?.colorAccent}
              colorTwo={factionsList[faction]?.colorGradient2}
              isLocked={isLocked(U4)}
            />
          </div>

          <div className="relative mb-6">
            <div
              className={cn(
                "absolute top-[5.125rem] left-[calc(50%-0.03125rem)] w-px h-[2.9375rem] transition-all duration-200",
                centerDownActive ? "bg-accent-solid" : "bg-[#212121]",
              )}
            />
            <FactionUpgradeNode
              level={levels[U5]}
              onClick={() => step(U5)}
              colorOne={factionsList[faction]?.colorAccent}
              colorTwo={factionsList[faction]?.colorGradient2}
              isLocked={isLocked(U5)}
            />
          </div>

          <div className="relative flex justify-between w-[16.0625rem] mb-[4.75rem]">
            <FactionImprovementsLine leftActive={row4LeftActive} rightActive={row4RightActive} />
            <FactionUpgradeNode
              level={levels[U6]}
              onClick={() => step(U6)}
              colorOne={factionsList[faction]?.colorAccent}
              colorTwo={factionsList[faction]?.colorGradient2}
              isLocked={isLocked(U6)}
            />
            <FactionUpgradeNode
              level={levels[U7]}
              onClick={() => step(U7)}
              colorOne={factionsList[faction]?.colorAccent}
              colorTwo={factionsList[faction]?.colorGradient2}
              isLocked={isLocked(U7)}
            />
          </div>

          <div>
            <FactionUpgradeNode
              level={levels[U8]}
              onClick={() => step(U8)}
              colorOne={factionsList[faction]?.colorAccent}
              colorTwo={factionsList[faction]?.colorGradient2}
              isLocked={isLocked(U8)}
            />
          </div>
        </div>
      </div>

      <div className="relative z-1 flex items-center mt-6 mx-6">
        <Button className="shrink-0 size-8 p-0" color="gray" variant="soft" onClick={onBack}>
          <TriangleLeftIcon className="size-4.5" />
        </Button>
        <p className="ml-2.5 text-14 font-bold leading-none uppercase">{t("faction.goBack")}</p>
        <div className="h-12 px-3 flex items-center ml-auto bg-dark-800 rounded-xl">
          <p className="text-11 font-bold text-white/70 uppercase">
            {t("faction.improvementPointsAvailable")}
          </p>
          <div className="ml-15 text-14 font-bold leading-none">
            {currentCount} / {maxCount}
          </div>
          <StarIcon className="size-3.5 ml-0.5 text-[#B8B8B8]" />
        </div>
      </div>

      <div className="flex items-center mt-auto mb-6 ml-4">
        <MouseIcon className="size-5" />
        <p className="max-w-[12.0625rem] ml-2.5 text-12 font-medium text-[#C0C0C0]">
          {t("faction.mouseDescription")}
        </p>
      </div>
    </div>
  );
}
